import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { seoApi } from "@/lib/seoApi";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Layers, Square, Play, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-sky-100 text-sky-800 border-sky-200",
  running: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-neutral-200 text-neutral-700 border-neutral-300",
  partial: "bg-orange-100 text-orange-800 border-orange-200",
};

function StatusBadge({ status }) {
  return <Badge variant="outline" className={STATUS_STYLES[status] || ""}>{status}</Badge>;
}

function ProgressBar({ done, failed, total }) {
  const pctDone = total ? (done / total) * 100 : 0;
  const pctFailed = total ? (failed / total) * 100 : 0;
  return (
    <div className="w-full h-2.5 bg-neutral-100 rounded-full overflow-hidden flex">
      <div className="h-full bg-emerald-500" style={{ width: `${pctDone}%` }} />
      <div className="h-full bg-rose-400" style={{ width: `${pctFailed}%` }} />
    </div>
  );
}

function BatchRow({ job, onAction }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      const { data } = await seoApi.get(`/seo/workflows/batches/${job.id}`);
      setDetail(data);
    } catch { /* transient */ }
  }, [job.id]);

  useEffect(() => {
    if (!open) return;
    loadDetail();
    const t = setInterval(loadDetail, 7000);
    return () => clearInterval(t);
  }, [open, loadDetail]);

  const act = async (action) => {
    setBusy(true);
    try {
      await seoApi.post(`/seo/workflows/batches/${job.id}/${action}`);
      toast.success(action === "stop" ? "Batch stopped (current article will finish)" : "Batch resumed");
      onAction();
      if (open) loadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  const done = job.tasks_completed || 0;
  const failed = job.tasks_failed || 0;
  const total = job.tasks_total || 0;
  const canStop = ["pending", "running"].includes(job.status);
  const canResume = ["cancelled", "failed", "partial"].includes(job.status);

  return (
    <div className="border border-neutral-200 rounded-lg p-4" data-testid={`batch-row-${job.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-medium">{job.name}</span>
        <StatusBadge status={job.status} />
        <span className="text-xs text-neutral-500">
          {done}/{total} done{failed ? ` · ${failed} failed/skipped` : ""}
        </span>
        {job.scheduled_at && (
          <span className="text-xs text-sky-700">
            ⏰ scheduled: {new Date(job.scheduled_at).toLocaleString()}
          </span>
        )}
        <div className="flex-1" />
        {canStop && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("stop")}
                  data-testid={`batch-stop-${job.id}`}>
            <Square size={13} /> Stop
          </Button>
        )}
        {canResume && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("resume")}
                  data-testid={`batch-resume-${job.id}`}>
            <Play size={13} /> Resume
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}
                data-testid={`batch-toggle-${job.id}`}>
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </Button>
      </div>
      <div className="mt-3">
        <ProgressBar done={done} failed={failed} total={total} />
      </div>
      <div className="mt-1 text-xs text-neutral-400">
        created {new Date(job.created_at).toLocaleString()}
        {job.completed_at ? ` · finished ${new Date(job.completed_at).toLocaleString()}` : ""}
      </div>
      {open && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          {!detail ? (
            <div className="text-sm text-neutral-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr>
                  <th className="text-left py-1 pr-3">#</th>
                  <th className="text-left py-1 pr-3">Brief / Run</th>
                  <th className="text-left py-1 pr-3">Status</th>
                  <th className="text-left py-1">Error</th>
                </tr>
              </thead>
              <tbody>
                {(detail.runs || []).map((r, i) => (
                  <tr key={r.id} className="border-t border-neutral-50">
                    <td className="py-1.5 pr-3 text-neutral-400">{i + 1}</td>
                    <td className="py-1.5 pr-3">
                      <Link to={`/seo-studio/runs/${r.id}`} className="hover:underline text-neutral-800">
                        {r.brief_title || r.brief_id || r.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-3"><StatusBadge status={r.status} /></td>
                    <td className="py-1.5 text-xs text-rose-600">{r.error ? String(r.error).slice(0, 120) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function Batches() {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await seoApi.get("/seo/workflows/batches");
      setJobs(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <div className="p-8 text-red-600" data-testid="batches-error">{error}</div>;

  return (
    <div className="p-6 space-y-6" data-testid="batches-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Layers size={22} /> Batch Generation
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Mass article generation — strictly sequential through the AI Jobs queue.
            Batches survive backend restarts; stop &amp; resume anytime.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="batches-refresh">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          {jobs === null ? (
            <div className="text-sm text-neutral-500 py-6 text-center">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="text-sm text-neutral-500 py-8 text-center">
              No batches yet. Select briefs on the <Link to="/seo-studio/briefs" className="underline">Briefs</Link> page
              and click “Queue batch”.
            </div>
          ) : (
            jobs.map((j) => <BatchRow key={j.id} job={j} onAction={load} />)
          )}
        </CardContent>
      </Card>
    </div>
  );
}
